// Global Instructions Rule Applied!
//
// Settings → Manage delegations.
//
// Two lists: granted-to-me (who has allowed me to Send As them) and
// granted-by-me (who I have allowed to Send As me, with revoke).
//
// Grant flow uses search_system_users_for_select to pick the grantee. The
// grantor is ALWAYS the current operator — non-repudiation per Decisions
// §6. The backend asserts this on POST /email/delegations.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Typography
} from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  fetchMyDelegations,
  grantDelegation,
  revokeDelegation,
  searchSystemUsers,
  fetchEligibleSenders
} from '../../email/services/emailService.ts'
import type {
  EmailDelegationsModel,
  EmailSenderOption
} from '../../../state_manager/data_models/email_campaign_grid_model.ts'

const { Text, Title } = Typography

export function DelegationsTab() {
  const [loading, setLoading] = useState(true)
  const [model, setModel] = useState<EmailDelegationsModel | null>(null)
  // The current operator's system_user_id, looked up via the eligible-senders
  // RPC (the self entry's value). Used as grantor on POST.
  const [myUserId, setMyUserId] = useState<string | null>(null)

  const [grantModalOpen, setGrantModalOpen] = useState(false)
  const [granting, setGranting] = useState(false)
  const [granteeSelection, setGranteeSelection] = useState<string | undefined>(undefined)
  const [searchResults, setSearchResults] = useState<EmailSenderOption[]>([])
  const [searching, setSearching] = useState(false)

  const [revokingId, setRevokingId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    const token = getAccessToken(store.getState().auth)
    const [delegationsRes, sendersRes] = await Promise.all([
      fetchMyDelegations(token),
      fetchEligibleSenders(token)
    ])
    if (delegationsRes.error) showErrorToast(delegationsRes.error)
    if (delegationsRes.data) setModel(delegationsRes.data)
    const self = (sendersRes.data || []).find((s) => s.is_self)
    setMyUserId(self?.value || null)
    setLoading(false)
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  // ─── Grant ───────────────────────────────────────────────────────────

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onSearch = useCallback((value: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(async () => {
      setSearching(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await searchSystemUsers(value, token)
        setSearchResults(res.data || [])
      } finally {
        setSearching(false)
      }
    }, 250)
  }, [])

  // Pre-populate the picker on open with a baseline list so it isn't empty.
  const openGrantModal = useCallback(() => {
    setGranteeSelection(undefined)
    setSearchResults([])
    setGrantModalOpen(true)
    onSearch('')
  }, [onSearch])

  const filteredSearchResults = useMemo(() => {
    // Hide yourself and anyone you've already granted from the picker.
    const alreadyGrantedTo = new Set(
      (model?.granted_by_me || []).map((d) => d.grantee_user_id)
    )
    return searchResults.filter(
      (r) => r.value !== myUserId && !alreadyGrantedTo.has(r.value)
    )
  }, [searchResults, myUserId, model])

  const handleGrant = useCallback(async () => {
    if (!myUserId) {
      showErrorToast('Cannot determine your user identity. Try reloading the page.')
      return
    }
    if (!granteeSelection) return
    setGranting(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await grantDelegation(
        { grantor_user_id: myUserId, grantee_user_id: granteeSelection },
        token
      )
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to grant delegation')
        return
      }
      toast.success('Delegation granted')
      setGrantModalOpen(false)
      await refresh()
    } finally {
      setGranting(false)
    }
  }, [myUserId, granteeSelection, refresh])

  // ─── Revoke ──────────────────────────────────────────────────────────

  const handleRevoke = useCallback(
    async (delegationId: string) => {
      setRevokingId(delegationId)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await revokeDelegation(delegationId, token)
        if (!res.success) {
          showErrorToast(res.error || 'Failed to revoke delegation')
          return
        }
        toast.success('Delegation revoked')
        await refresh()
      } finally {
        setRevokingId(null)
      }
    },
    [refresh]
  )

  return (
    <Spin spinning={loading}>
      <div style={{ padding: '8px 0 24px 0' }}>
        <Alert
          showIcon
          type='info'
          style={{ marginBottom: 16 }}
          message='Revocations take effect at queue time'
          description='Revoking a grant prevents new sends as that person. A campaign already queued continues; if you need to stop one in flight, contact the operator.'
        />

        <Row gutter={20}>
          <Col xs={24} md={12}>
            <Card
              title='Granted to me'
              extra={<Text type='secondary'>I can Send as these people</Text>}
            >
              {(model?.granted_to_me || []).length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description='Nobody has delegated to you yet.'
                />
              ) : (
                <List
                  dataSource={model?.granted_to_me || []}
                  rowKey='id'
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={item.grantor_label}
                        description={item.grantor_email || ''}
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card
              title='Granted by me'
              extra={
                <Button
                  type='primary'
                  size='small'
                  icon={<PlusOutlined />}
                  onClick={openGrantModal}
                >
                  Grant
                </Button>
              }
            >
              {(model?.granted_by_me || []).length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description='You haven’t granted any delegations.'
                />
              ) : (
                <List
                  dataSource={model?.granted_by_me || []}
                  rowKey='id'
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Popconfirm
                          key='revoke'
                          title='Revoke this delegation?'
                          okText='Revoke'
                          okButtonProps={{ danger: true }}
                          onConfirm={() => handleRevoke(item.id)}
                        >
                          <Button
                            danger
                            type='text'
                            size='small'
                            icon={<DeleteOutlined />}
                            loading={revokingId === item.id}
                          />
                        </Popconfirm>
                      ]}
                    >
                      <List.Item.Meta
                        title={item.grantee_label}
                        description={item.grantee_email || ''}
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </Col>
        </Row>
      </div>

      <Modal
        title='Grant Send-as delegation'
        open={grantModalOpen}
        onCancel={() => setGrantModalOpen(false)}
        onOk={handleGrant}
        okText='Grant'
        okButtonProps={{ disabled: !granteeSelection, loading: granting }}
      >
        <Space direction='vertical' style={{ width: '100%' }}>
          <Text>
            Allow another user to send emails as you. They will appear in your
            grantee's Send-as picker until you revoke.
          </Text>
          <Select
            showSearch
            value={granteeSelection}
            onChange={setGranteeSelection}
            onSearch={onSearch}
            filterOption={false}
            loading={searching}
            placeholder='Search a person…'
            // Force the popup to render INSIDE the modal so AntD's dropdown
            // z-index can't slip beneath the modal's own stacking context.
            getPopupContainer={(trigger) =>
              (trigger.parentElement as HTMLElement) || document.body
            }
            options={filteredSearchResults.map((r) => ({
              value: r.value,
              label: `${r.label}${r.email ? `  ·  ${r.email}` : ''}`
            }))}
            style={{ width: '100%' }}
          />
        </Space>
      </Modal>
    </Spin>
  )
}
